import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Minus, ShoppingBag, ArrowRight, X, Check } from "lucide-react";
import { api } from "@/lib/api";
import { gbp, makeEconomyItem } from "@/lib/pricing";
import { useScrolling } from "@/lib/useScrolling";
import { useCart } from "@/context/CartContext";
import { useCategories } from "@/context/SiteContentContext";
import { BasketPanel } from "@/components/BasketPanel";

const round2 = (n) => Math.round(n * 100) / 100;
const boostShort = (code) => (code === "x2" ? "×2" : code === "x3" ? "×3" : "Std");

export default function EconomyBuilder({ onProgress } = {}) {
  const cats = useCategories();
  const { items, addItem, bumpCart } = useCart();
  const nav = useNavigate();
  const scrolling = useScrolling();

  const [flavors, setFlavors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [trade, setTrade] = useState(false);

  const [sizeId, setSizeId] = useState(null);
  const [count, setCount] = useState(1);
  const [slots, setSlots] = useState([]); // [{ flavor, boost }]
  const [pickerSlot, setPickerSlot] = useState(null);
  const [pickerBoost, setPickerBoost] = useState("standard");

  const addBtnRef = useRef(null);
  const cartTargetRef = useRef(null);
  const [fly, setFly] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/economy", { params: { include_unavailable: true } }), api.get("/flavors")]).then(([e, f]) => {
      setSizes(e.data.sizes || []);
      setBoosts(e.data.boosts || []);
      setTrade(!!e.data.trade);
      setFlavors((f.data || []).filter((x) => x.range_type === "economy"));
    });
  }, []);

  const boostMap = useMemo(() => Object.fromEntries(boosts.map((b) => [b.code, b])), [boosts]);
  const boostMultOf = (code) => Number(boostMap[code]?.mult) || 1;
  const boostLabelOf = (code) => boostMap[code]?.label || "Standard";
  const size = useMemo(() => sizes.find((s) => s.id === sizeId), [sizes, sizeId]);
  const baseUnit = size ? size.price : 0;

  const filled = slots.filter(Boolean).length;
  useEffect(() => { onProgress?.(filled > 0); }, [filled, onProgress]);
  const boxTotal = useMemo(() => {
    let t = 0;
    for (let k = 0; k < count; k += 1) { const sl = slots[k]; t += baseUnit * (sl ? boostMultOf(sl.boost) : 1); }
    return round2(t);
  }, [slots, count, baseUnit]); // eslint-disable-line react-hooks/exhaustive-deps
  const anyBoosted = slots.some((sl) => sl && sl.boost && sl.boost !== "standard");
  const remaining = count - filled;
  const complete = filled === count && count > 0;

  const setBottleCount = (n) => {
    const v = Math.max(1, Math.min(999, n || 1));
    setCount(v);
    setSlots((prev) => prev.slice(0, v));
    setPickerSlot(null);
  };
  const changeSize = (id) => {
    const next = sizes.find((s) => s.id === id);
    if (next && next.available === false) { toast.message(`${next.label} is sold out — back soon`); return; }
    setSizeId(id); setPickerSlot(null);
  };
  const openPicker = (i, boost) => { setPickerBoost(boost || "standard"); setPickerSlot(i); };
  const flavourAllows = (f, boost) => boost === "standard" || !Array.isArray(f.enabled_strengths) || f.enabled_strengths.includes(boost);
  const chooseFlavour = (i, f, boost) => {
    if (f.stock <= 0) { toast.error(`${f.name} is out of stock`); return; }
    if (!flavourAllows(f, boost)) { toast.error(`${f.name} is not available in ${boostShort(boost)} right now — switch to Standard`); return; }
    const arr = Array.from({ length: count }, (_, k) => slots[k] || null);
    arr[i] = { flavor: f, boost: boost || "standard" };
    setSlots(arr);
    setPickerSlot(null);
  };
  const removeSlot = (idx) => setSlots((s) => { const next = [...s]; next[idx] = null; return next; });
  const fillAllSame = () => {
    const first = slots.find(Boolean);
    if (!first) { toast.error("Fill one bottle first, then copy it to the rest"); return; }
    setSlots(Array.from({ length: count }, (_, k) => slots[k] || { ...first }));
  };

  const addBoxToOrder = () => {
    if (!complete) { toast.error(`Fill all ${count} bottles first`); return false; }
    const filledSlots = slots.filter(Boolean);
    const lastColor = filledSlots[filledSlots.length - 1]?.flavor?.accent_color || "#9AA0A6";
    const s = addBtnRef.current?.getBoundingClientRect();
    const t = cartTargetRef.current?.getBoundingClientRect();
    if (s && t) setFly({ id: Date.now(), x0: s.left + s.width / 2 - 12, y0: s.top - 4, x1: t.left + t.width / 2 - 12, y1: t.top, color: lastColor });
    const counts = {};
    filledSlots.forEach(({ flavor, boost }) => {
      const bo = boost || "standard";
      const k = `${flavor.id}-${bo}`;
      const unit = round2(baseUnit * boostMultOf(bo));
      counts[k] = counts[k] || { flavor_id: flavor.id, flavor_name: flavor.name, boost: bo, boost_label: boostLabelOf(bo), accent_color: flavor.accent_color, quantity: 0, unit_price: unit, line_total: 0 };
      counts[k].quantity += 1;
      counts[k].line_total = round2(counts[k].unit_price * counts[k].quantity);
    });
    const selections = Object.values(counts);
    addItem(makeEconomyItem(size, selections));
    toast.success(`Added ${count} × ${size?.label} Economy box to your order`);
    setSlots([]); setCount(1); setPickerSlot(null);
    return true;
  };
  const addAndCheckout = () => { if (addBoxToOrder()) nav("/checkout"); };

  if (sizes.length === 0) return <div className="flex h-[50vh] items-center justify-center mono text-xs uppercase tracking-widest text-muted-foreground">Loading {cats.economy.title}…</div>;

  return (
    <div className="mx-auto max-w-[1300px] px-6 pt-2 pb-28 lg:pb-12" data-testid="economy-page">
      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_340px]">
        <div>
          <div className={`border bg-card p-4 transition-colors ${!size || complete ? "border-border" : "border-primary/50 ring-1 ring-primary/20"}`} data-testid="economy-fill-panel">
            {!size ? (
              <div data-testid="economy-size-options">
                <div className="flex items-center gap-2.5">
                  <span className="mono flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-black text-primary-foreground">1</span>
                  <span className="font-heading text-xl font-black tracking-tight">Choose your bottle size</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Two budget flavours, one base, flat low prices. Pick a size, choose how many bottles, then fill each with a flavour and strength.</p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="economy-size-selector">
                  {sizes.map((s) => (
                    <button key={s.id} data-testid={`economy-size-${s.label}`} onClick={() => changeSize(s.id)}
                      className={`flex items-center justify-between gap-3 border px-4 py-3 text-left transition-colors ${s.available === false ? "cursor-not-allowed border-border/50 opacity-50 grayscale" : sizeId === s.id ? "border-primary bg-primary/10" : "border-border hover:border-primary"}`} aria-disabled={s.available === false}>
                      <div className="min-w-0">
                        {s.available === false ? (
                          <span data-testid={`economy-size-sold-out-${s.label}`} className="mono mb-1 inline-block rounded-full bg-muted px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-muted-foreground">Sold out · back soon</span>
                        ) : trade && s.retail_price > s.price ? (
                          <span data-testid={`economy-vs-retail-${s.label}`} className="mono mb-1 inline-block rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-primary">save {Math.round((1 - s.price / s.retail_price) * 100)}% vs retail</span>
                        ) : (
                          <span className="mono mb-1 inline-block rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-primary">economy price</span>
                        )}
                        <div className="font-heading text-xl font-black tracking-tighter">{s.label}</div>
                        <div className="mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{s.ml} ml · Strawberry Vanilla or Unflavoured</div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <div className="text-right leading-tight">
                          <div className="mono text-[10px] text-muted-foreground">per bottle</div>
                          <span className="mono text-sm font-bold text-primary">{gbp(s.price)}</span>
                          {trade && s.retail_price > s.price && <div className="mono text-[10px] text-muted-foreground line-through">{gbp(s.retail_price)} retail</div>}
                        </div>
                        {sizeId === s.id && <Check className="h-4 w-4 text-primary" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2" data-testid="economy-size-chip-row">
                  <span className="mono mr-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Bottle size</span>
                  <div className="flex flex-wrap gap-1.5">
                    {sizes.map((s) => (
                      <button key={s.id} data-testid={`economy-size-${s.label}`} onClick={() => changeSize(s.id)} aria-disabled={s.available === false} title={s.available === false ? "Sold out — back soon" : undefined}
                        className={`mono border px-3 py-1.5 text-[11px] font-bold transition-colors ${s.available === false ? "cursor-not-allowed border-border/50 line-through opacity-40" : sizeId === s.id ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-border pt-4" data-testid="economy-count-panel">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">How many {size.label} bottles?</div>
                    <div className="mt-1.5 flex w-fit items-center border border-border">
                      <button data-testid="economy-count-minus" onClick={() => setBottleCount(count - 1)} className="p-2.5 hover:bg-muted"><Minus className="h-4 w-4" /></button>
                      <input data-testid="economy-count-input" type="number" min="1" value={count} onChange={(e) => setBottleCount(parseInt(e.target.value))} className="w-16 border-x border-border bg-transparent py-2 text-center mono" />
                      <button data-testid="economy-count-plus" onClick={() => setBottleCount(count + 1)} className="p-2.5 hover:bg-muted"><Plus className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Per bottle</div>
                    <div className="mono mt-1 font-heading text-2xl font-black leading-none text-primary">{gbp(baseUnit)}</div>
                    <div className="mono mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">flat economy price</div>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Box total</div>
                    <div className="mono mt-1 font-heading text-2xl font-black leading-none text-primary" data-testid="economy-box-total">{gbp(boxTotal)}</div>
                    <div className="mono mt-1 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{count} × {size.label}{anyBoosted ? " · incl. strength" : ""}</div>
                  </div>
                </div>

                <div className="mt-4 border-t border-border pt-4">
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                    <div className="flex items-center gap-2.5">
                      <span className="font-heading text-lg font-black tracking-tight">Fill each bottle</span>
                      <span className="mono text-[11px] text-muted-foreground" data-testid="economy-fill-progress">{filled}/{count} filled</span>
                    </div>
                    {filled > 0 && (
                      <button data-testid="economy-fill-all-same" onClick={fillAllSame} className="mono border border-border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-primary transition-colors hover:border-primary">Fill rest with the first bottle</button>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">Click a bottle to choose its flavour and strength. {cats.economy.title} use a single base, so there's no base to pick.</p>

                  <div className="mt-3">
                    {pickerSlot !== null && (
                      <div className="fixed inset-0 z-40 bg-black/50" onClick={() => setPickerSlot(null)} data-testid="economy-picker-backdrop" />
                    )}
                    <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 lg:grid-cols-8" data-testid="economy-slot-grid">
                      {Array.from({ length: count }).map((_, i) => {
                        const slot = slots[i];
                        const open = pickerSlot === i;
                        return (
                          <div key={i} className="relative">
                            {slot ? (
                              <div className="relative flex aspect-square cursor-pointer flex-col overflow-hidden border" style={{ borderColor: slot.flavor.accent_color }}
                                onClick={() => openPicker(i, slot.boost)} data-testid={`economy-slot-tile-${i}`}>
                                <div className="relative min-h-0 flex-1 bg-white">
                                  {slot.flavor.image_url ? <img src={slot.flavor.image_url} alt={slot.flavor.name} className="h-full w-full object-cover" /> : <div className="h-full w-full" style={{ background: slot.flavor.accent_color }} />}
                                  {slot.boost && slot.boost !== "standard" && (
                                    <span className="mono absolute left-1 top-1 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary-foreground shadow">{boostShort(slot.boost)}</span>
                                  )}
                                  <button onClick={(e) => { e.stopPropagation(); removeSlot(i); setPickerSlot(null); }} data-testid={`economy-remove-slot-${i}`} className="absolute right-1 top-1 rounded-full bg-black/50 p-0.5 text-white hover:bg-black/80"><X className="h-3 w-3" /></button>
                                </div>
                                <div className="px-1.5 py-1" style={{ background: slot.flavor.accent_color }}>
                                  <span className="truncate text-[9px] font-bold leading-tight text-white drop-shadow">{slot.flavor.name}</span>
                                </div>
                              </div>
                            ) : (
                              <button onClick={() => openPicker(i, "standard")} data-testid={`economy-slot-tile-${i}`}
                                className={`relative flex aspect-square w-full flex-col items-center justify-center gap-1 overflow-hidden border border-dashed p-1 text-center transition-colors hover:border-primary hover:bg-muted ${open ? "border-primary bg-muted" : "border-border"}`}>
                                <Plus className="h-4 w-4 text-primary" />
                                <span className="mono text-[9px] font-bold uppercase leading-tight tracking-[0.1em] text-primary">Choose flavour</span>
                              </button>
                            )}
                            {open && (
                              <div className="fixed left-1/2 top-1/2 z-50 w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 border border-border bg-card p-3 text-left shadow-2xl shadow-black/60" data-testid={`economy-slot-picker-${i}`} onClick={(e) => e.stopPropagation()}>
                                <div className="mono mb-1.5 flex items-center justify-between text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                                  <span>Bottle {i + 1} · Strength</span>
                                  <button onClick={() => setPickerSlot(null)} data-testid={`economy-picker-close-${i}`} className="rounded-full p-0.5 hover:bg-muted"><X className="h-3.5 w-3.5" /></button>
                                </div>
                                <div className="mb-3 flex gap-1" data-testid={`economy-picker-boost-row-${i}`}>
                                  {boosts.map((b) => (
                                    <button key={b.code} onClick={() => setPickerBoost(b.code)} data-testid={`economy-picker-boost-${i}-${b.code}`}
                                      className={`flex flex-1 flex-col items-center border px-1 py-1.5 text-xs font-medium transition-colors ${pickerBoost === b.code ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
                                      <span>{b.short}</span>
                                      <span className="mono text-[10px] opacity-80">{`+${gbp(b.mult === 1 ? 0 : round2(baseUnit * (b.mult - 1)))}`}</span>
                                    </button>
                                  ))}
                                </div>
                                <div className="mono mb-1.5 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Flavour</div>
                                <div className="grid grid-cols-2 gap-1.5" data-testid={`economy-picker-flavor-grid-${i}`}>
                                  {flavors.map((f) => {
                                    const allowed = flavourAllows(f, pickerBoost);
                                    return (
                                    <button key={f.id} disabled={f.stock <= 0 || !allowed} data-testid={`economy-picker-flavor-${i}-${f.id}`}
                                      onClick={() => chooseFlavour(i, f, pickerBoost)}
                                      className="flex items-center gap-2 border border-border p-2 text-left transition-colors hover:border-primary hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 disabled:grayscale">
                                      <span className="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-border bg-white">
                                        {f.image_url ? <img src={f.image_url} alt="" className="h-full w-full object-cover" /> : <span className="block h-full w-full rounded-full" style={{ background: f.accent_color }} />}
                                      </span>
                                      <span className="flex flex-col text-[11px] font-medium leading-tight">{f.name}
                                        {f.stock <= 0 && <span className="mono text-[8px] font-bold uppercase tracking-[0.1em] text-destructive" data-testid={`economy-flavor-sold-out-${f.id}`}>Sold out</span>}
                                        {f.stock > 0 && !allowed && <span className="mono text-[8px] font-bold uppercase tracking-[0.1em] text-muted-foreground" data-testid={`economy-flavor-no-boost-${f.id}`}>{(f.enabled_strengths || []).length <= 1 ? "Standard only" : `No ${boostShort(pickerBoost)}`}</span>}
                                      </span>
                                    </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 h-fit" data-testid="economy-summary">
          <div className="border border-border bg-card p-5">
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Your Economy Box</div>
            {!size ? (
              <p className="mt-3 text-sm text-muted-foreground" data-testid="economy-summary-empty">Choose a bottle size to begin.</p>
            ) : (
              <>
                <div className="mt-2 font-heading text-2xl font-black tracking-tighter" data-testid="economy-heading">{count} × {size.label}</div>
                <div className="mono mt-1 text-[11px] text-muted-foreground">{complete ? "All bottles flavoured ✓" : `${filled}/${count} flavoured`}</div>
                <div className="mt-4 border-t border-border pt-4">
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">{gbp(baseUnit)} × {count} bottle{count === 1 ? "" : "s"}</span><span className="mono font-bold">{gbp(round2(baseUnit * count))}</span></div>
                  {anyBoosted && <div className="mt-1.5 flex justify-between text-sm"><span className="text-muted-foreground">Strength boosts</span><span className="mono font-bold text-primary">+{gbp(round2(boxTotal - baseUnit * count))}</span></div>}
                  <div className="mt-3 flex items-end justify-between border-t border-border pt-3">
                    <span className="text-sm font-semibold">Total</span>
                    <span className="font-heading text-3xl font-black text-primary mono" data-testid="economy-total">{gbp(boxTotal)}</span>
                  </div>
                  <div className="mono mt-1 text-[10px] text-muted-foreground">before shipping · crypto only</div>
                </div>
                {complete ? (
                  <>
                    <button ref={addBtnRef} data-testid="economy-add-and-checkout-btn" onClick={addAndCheckout}
                      className="mt-4 flex w-full items-center justify-center gap-2 bg-primary py-4 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5">
                      Checkout with this box — {gbp(boxTotal)} <ArrowRight className="h-4 w-4" />
                    </button>
                    <button data-testid="economy-add-box-btn" onClick={addBoxToOrder}
                      className="mono mt-2 flex w-full items-center justify-center gap-2 border border-border py-3 text-[11px] font-bold uppercase tracking-[0.15em] transition-colors hover:border-primary hover:text-primary">
                      <Plus className="h-3.5 w-3.5" /> Add box &amp; build another
                    </button>
                  </>
                ) : (
                  <>
                    <button ref={addBtnRef} data-testid="economy-add-box-btn" disabled
                      className="mt-4 flex w-full items-center justify-center gap-2 bg-primary py-4 text-sm font-semibold text-primary-foreground opacity-40">
                      <Plus className="h-4 w-4" /> Add to Order
                    </button>
                    <p className="mono mt-2 text-center text-[11px] text-muted-foreground" data-testid="economy-blocked">{remaining} more bottle{remaining === 1 ? "" : "s"} to fill before you can add</p>
                  </>
                )}
              </>
            )}
            <div ref={cartTargetRef}><BasketPanel /></div>
          </div>
        </aside>
      </div>

      <AnimatePresence>
        {fly && (
          <motion.div key={fly.id}
            initial={{ x: fly.x0, y: fly.y0, opacity: 1, scale: 1 }}
            animate={{ x: fly.x1, y: fly.y1, opacity: [1, 1, 0.2], scale: [1, 1.15, 0.35] }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            onAnimationComplete={() => { bumpCart(); setFly(null); }}
            style={{ position: "fixed", left: 0, top: 0, zIndex: 100, pointerEvents: "none" }}>
            <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white shadow-xl" style={{ background: fly.color }} />
          </motion.div>
        )}
      </AnimatePresence>

      {size && (
        <div className={`fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 px-4 py-3 backdrop-blur-xl transition-transform duration-300 lg:hidden ${scrolling ? "translate-y-full" : "translate-y-0"}`} data-testid="economy-mobile-action-bar">
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">{complete ? "Box ready" : `${filled}/${count} filled · ${remaining} to go`}</div>
              <div className="font-heading text-2xl font-black leading-none text-primary mono">{gbp(boxTotal)}</div>
            </div>
            {items.length > 0 && (
              <button onClick={() => nav("/cart")} data-testid="economy-mobile-view-cart" className="relative flex shrink-0 items-center gap-1.5 border border-border px-3 py-2.5 text-xs font-medium">
                <ShoppingBag className="h-4 w-4" /><span className="mono">{items.reduce((s, i) => s + i.quantity, 0)}</span>
              </button>
            )}
            <button onClick={addBoxToOrder} disabled={!complete} data-testid="economy-mobile-add-box"
              className="flex shrink-0 items-center gap-2 bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-transform enabled:hover:-translate-y-0.5 disabled:opacity-40">
              {complete ? <><Plus className="h-4 w-4" /> Add to Order</> : `${remaining} more`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
